/**
 * chunkReload — JEDINÝ zdroj pravdy pro detekci a zotavení ze "stale chunk"
 * problému po deployi.
 *
 * Po deployi mají JS chunky nové hashe. Tab se starým (in-memory/cached)
 * index.html chce načíst mrtvý chunk → server vrátí HTML → browser hlásí
 * "'text/html' is not a valid JavaScript MIME type" / ChunkLoadError.
 *
 * Zotavení = JEDEN cache-busting reload, sdílený main.tsx + lazyWithRetry +
 * PageErrorBoundary. Loop-guard je ČASOVÝ (ne sticky session flag):
 *  - reload smí proběhnout max 1× za RELOAD_COOLDOWN_MS → zabrání smyčce;
 *  - po vypršení cooldownu (nebo po úspěšném načtení chunku) se může zotavit
 *    znovu → SELF-HEALING (sticky guard se mohl zaseknout napořád a uživatel
 *    pak viděl error boundary do zavření tabu — to byl bug).
 *  - protože VŠECHNY tři mechanismy dělají stejný cache-busting reload, nevadí
 *    který vyhraje (žádný „plain reload krade cooldown" race jako dřív).
 */

const RELOAD_TS_KEY = '__rapio_chunk_reload_ts';
const RELOAD_COOLDOWN_MS = 30_000;

export function isChunkLoadError(err: unknown): boolean {
  const msg = String((err as any)?.message ?? err ?? '').toLowerCase();
  return (
    msg.includes('failed to fetch dynamically imported module') ||
    msg.includes('dynamically imported module') ||
    msg.includes("'text/html' is not a valid javascript") ||
    msg.includes('is not a valid javascript mime type') ||
    msg.includes('chunkloaderror') ||
    msg.includes('importing a module script failed') ||
    msg.includes('mime type') ||
    msg.includes('loading chunk') ||
    msg.includes('loading css chunk')
  );
}

function lastReloadAt(): number {
  try { return Number(sessionStorage.getItem(RELOAD_TS_KEY)) || 0; } catch { return 0; }
}

/** True, pokud od posledního stale-chunk reloadu uplynul cooldown (lze zkusit). */
export function canAttemptChunkReload(): boolean {
  return Date.now() - lastReloadAt() > RELOAD_COOLDOWN_MS;
}

/** Volá se po ÚSPĚŠNÉM načtení chunku → vynuluje cooldown (příští deploy se zotaví hned). */
export function clearChunkReloadGuard(): void {
  try { sessionStorage.removeItem(RELOAD_TS_KEY); } catch { /* ignore */ }
}

function cacheBustReplace(): void {
  try {
    const url = new URL(window.location.href);
    url.searchParams.set('_cb', String(Date.now()));
    window.location.replace(url.toString());
  } catch {
    window.location.reload();
  }
}

/**
 * Cache-busting reload s časovým loop-guardem. Vrací true = reload spuštěn,
 * false = v cooldownu (právě jsme reloadli) → ukaž chybu (uživatel může „Zkusit znovu").
 */
export function attemptChunkReload(): boolean {
  if (!canAttemptChunkReload()) return false;
  try { sessionStorage.setItem(RELOAD_TS_KEY, String(Date.now())); } catch { /* ignore */ }
  cacheBustReplace();
  return true;
}

/** Vynucený reload na přání uživatele (tlačítko) — ignoruje cooldown. */
export function forceReloadNow(): void {
  clearChunkReloadGuard();
  cacheBustReplace();
}
